'use client';

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faGlobe, faChartLine } from '@fortawesome/pro-regular-svg-icons';
import useAnalytics from 'src/hooks/analytics/useAnalytics';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import usePulseTableColumns from 'src/hooks/table/usePulseTableColumns';
import { TableRowActions } from '..';
import {
  Tooltip,
  PulseHeader,
  PulseCell,
  PulseColumnMenu,
  PulseColumnDef,
  PulseLoading,
} from 'src/components';
import { AnalyticsErrorOverlay } from '../AnalyticsErrorOverlay';
import { AnalyticsTableHeader } from '../AnalyticsTableHeader';
import { ANALYTICS_LOCATION } from 'src/hooks/filters/constants';
import { useGetPlaceOfServiceAnalyticsQuery } from 'src/api/redux';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { SortSection } from '../AnalyticsTrendsTable/SortSection';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { getNewExport } from 'src/api';
import { AnalyticsResultItem } from '../types';
import { ANALYTICS_DEFAULT_DATE_RANGE } from 'src/components/constants';

// Type for geography/territory data with place of service breakdown returned from API
type PlaceOfServiceResultItem = AnalyticsResultItem<{
  claims?: Record<string, number>;
  patients?: Record<string, number>;
}>;

const AnalyticsPlaceOfServiceTable = () => {
  const log = useLogging();
  const { setNotification } = useNotification();

  // Redux state and actions (ANALYTICS_LOCATION.SITES_OF_SERVICE location)
  const {
    selectedGeographyObjects = [],
    selectedCodeSet = [],
    formattedFiltersForAPI,
    date_range = ANALYTICS_DEFAULT_DATE_RANGE,
    volumeTypes = [],
    modeType,
    volumeConfigs = {},
  } = useAnalytics(ANALYTICS_LOCATION.PLACE_OF_SERVICE) || {};

  const [sort, setSort] = useState<{
    sort: string;
    order: 'asc' | 'desc';
  } | null>({
    sort: 'name',
    order: 'asc',
  });

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  useEffect(() => {
    if (sort && sort.sort !== 'name') {
      const currentVolumeType = volumeTypes?.[0] || 'claims';
      if (!sort.sort.includes(currentVolumeType)) {
        setSort({
          sort: 'name',
          order: 'asc',
        });
      }
    }
  }, [volumeTypes, sort]);

  const shouldFetch = useMemo(() => {
    if (!selectedCodeSet) return false;
    return Array.isArray(selectedGeographyObjects) && selectedGeographyObjects.length > 0 && selectedCodeSet?.length > 0;
  }, [selectedGeographyObjects, selectedCodeSet]);

  // Build volume_configs for API - Sites of Service table can have multiple sites of service selected
  const apiVolumeConfigs = useMemo(() => {
    if (!selectedCodeSet || selectedCodeSet.length === 0) return {};

    return selectedCodeSet.reduce((acc, codeSet) => {
      const codeSetId = codeSet.id.toString();

      const config = {
        code_set: codeSet.id,
        mode: modeType || 'treating',
      };

      acc[codeSetId] = config;
      return acc;
    }, {} as Record<string, any>);
  }, [selectedCodeSet, volumeConfigs, volumeTypes, date_range, modeType]);

  const {
    data: placeOfServiceData,
    isLoading: isLoadingPayerMix,
    isFetching: isFetchingPayerMix,
    isError: isErrorPlaceOfService,
    error: errorPlaceOfService,
  } = useGetPlaceOfServiceAnalyticsQuery(
    {
      params: {
        sort: sort?.sort,
        order: sort?.order,
      },
      data: {
        volume_types: volumeTypes.length > 0 ? volumeTypes : ['claims'],
        date_range: {
          gte: parseInt(date_range.gte),
          lte: parseInt(date_range.lte),
        },
        volume_configs: apiVolumeConfigs,
        geographies: selectedGeographyObjects.map((g) => ({
          id: g.id,
          type: g.type,
        })),
        ...(formattedFiltersForAPI.length > 0 && {
          filters: formattedFiltersForAPI,
        }),
      },
    },
    {
      skip: !shouldFetch,
    }
  );

  const { closeAllButCurrent } = useDrawer();

  const { dataColumns, dataKeys } = useMemo(() => {
    const placesOfService = placeOfServiceData?.places_of_service || {};

    const sortedEntries = Object.entries(placesOfService).sort((a, b) =>
      String(a[1]).localeCompare(String(b[1]))
    );

    return {
      dataColumns: sortedEntries.map(([, description]) => String(description)),
      dataKeys: sortedEntries.map(([code]) => String(code)),
    };
  }, [placeOfServiceData?.places_of_service]);

  // Define columns using PulseColumnDef for resizing (excluding Geography which is rendered separately)
  const columns: PulseColumnDef[] = useMemo(() => {
    return dataKeys.map((key, i) => ({
      field: key,
      headerName: dataColumns[i],
      width: 200,
      minWidth: 150,
      maxWidth: 400,
      resizable: true,
      sortable: true,
    }));
  }, [dataKeys, dataColumns]);

  // Use column resizing hook
  const { startResizing, isResizing, getGridTemplateColumns } =
    usePulseTableColumns({ columns });

  const displayedGeographies = useMemo((): PlaceOfServiceResultItem[] => {
    const geographies = Array.isArray(selectedGeographyObjects) ? selectedGeographyObjects : [];

    if (placeOfServiceData?.results && placeOfServiceData.results.length > 0) {
      const selectedIds = new Set<string | number>(
        geographies.map((g) => g.id)
      );

      const sortedResults = placeOfServiceData.results.filter(
        (result: PlaceOfServiceResultItem) => selectedIds.has(result.id)
      );

      const resultsMap = new Map<string | number, PlaceOfServiceResultItem>(
        placeOfServiceData.results.map((result: PlaceOfServiceResultItem) => [
          result.id,
          result,
        ])
      );

      const missingGeographies = geographies
        .filter((geography) => !resultsMap.has(geography.id))
        .map(
          (geography): PlaceOfServiceResultItem => ({
            id: geography.id,
            name: geography.name || '',
            type: geography.type,
          })
        );

      return [...sortedResults, ...missingGeographies];
    }

    return geographies.map(
      (geography): PlaceOfServiceResultItem => ({
        id: geography.id,
        name: geography.name || '',
        type: geography.type,
      })
    );
  }, [placeOfServiceData, selectedGeographyObjects]);

  // Handle table export
  const handleExport = useCallback(
    async (format: 'csv' | 'xlsx') => {
      try {
        // Build query params for the export API
        const queryParams = new URLSearchParams({
          sort: sort?.sort || 'name',
          order: sort?.order || 'asc',
        });

        await getNewExport({
          url: `https://api.medscout.io/api/v1/analytics/territory-insights/place-of-service/?${queryParams.toString()}`,
          method: 'POST',
          fileFormat: format,
          columns: ['id', 'name', 'type', `${volumeTypes?.[0]}`],
          fileName: `analytics-place-of-service-${
            new Date().toISOString().split('T')[0]
          }`,
          data: {
            volume_types: volumeTypes.length > 0 ? volumeTypes : ['claims'],
            date_range: {
              gte: parseInt(date_range.gte),
              lte: parseInt(date_range.lte),
            },
            volume_configs: apiVolumeConfigs,
            geographies: selectedGeographyObjects.map((g) => ({
              id: g.id,
              type: g.type,
            })),
            ...(formattedFiltersForAPI.length > 0 && {
              filters: formattedFiltersForAPI,
            }),
          },
        });

        setNotification({
          title: 'Success',
          message:
            'Export initiated - You will receive an email when it is ready for download',
          type: 'success',
        });

        log.event('Export', {
          type: ANALYTICS_LOCATION.PLACE_OF_SERVICE,
          source: 'Analytics Place Of Service Table',
          columns: columns.map((col) => col.field),
          count: displayedGeographies.length,
          format,
        });
      } catch (err) {
        log.exception(`Error exporting place of service: ${err}`);

        setNotification({
          title: 'Error',
          message: err?.error_message || 'Error exporting, please try again.',
          type: 'error',
        });
      }
    },
    [
      columns,
      volumeTypes,
      date_range,
      modeType,
      selectedGeographyObjects,
      formattedFiltersForAPI,
      sort,
      log,
      setNotification,
      displayedGeographies,
    ]
  );

  const handleOpenTerritoryDrawer = useCallback(() => {
    log.event('Territory drawer opened', {
      source: 'AnalyticsPlaceOfServiceTable',
    });
    closeAllButCurrent('analytics-territory-drawer');
  }, [closeAllButCurrent, log]);

  const handleEllipsisClick = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      e.stopPropagation();
      const isOpening = openMenuId !== columnId;
      if (isOpening) {
        log.event('Column menu opened', {
          source: 'AnalyticsPlaceOfServiceTable',
          columnId,
        });
      }
      setOpenMenuId((prev) => (prev === columnId ? null : columnId));
    },
    [openMenuId, log]
  );

  const handleSortToggle = useCallback(
    (columnName: string, order?: 'asc' | 'desc') => {
      setSort((prevSort) => {
        const newOrder =
          order ||
          (prevSort?.sort === columnName
            ? prevSort.order === 'asc'
              ? 'desc'
              : 'asc'
            : 'desc');
        log.event('Sort changed', {
          source: 'AnalyticsPlaceOfServiceTable',
          columnName,
          sortOrder: newOrder,
        });
        if (order) {
          return {
            sort: columnName,
            order,
          };
        }
        if (prevSort?.sort === columnName) {
          return {
            sort: columnName,
            order: prevSort.order === 'asc' ? 'desc' : 'asc',
          };
        }
        return {
          sort: columnName,
          order: 'desc',
        };
      });
      setOpenMenuId(null);
    },
    [log]
  );

  const hasGeographies = Array.isArray(selectedGeographyObjects) && selectedGeographyObjects.length > 0;
  const hasCodeSets = selectedCodeSet?.length > 0;
  const showGeographyEmptyState = !hasGeographies;
  const showCodeSetEmptyState = !hasCodeSets;
  const showLoadingOverlay =
    (isLoadingPayerMix || isFetchingPayerMix) && hasGeographies && hasCodeSets;

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden bg-white relative rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10),0px_1px_2px_-1px_rgba(0,0,0,0.00)] border border-neutral-200"
      data-testid="analytics-place-of-service-table"
    >
      <AnalyticsTableHeader
        columns={dataColumns}
        location={ANALYTICS_LOCATION.PLACE_OF_SERVICE}
        onExport={handleExport}
      />

      <div
        className={`flex-1 relative ${
          hasGeographies
            ? 'overflow-x-auto overflow-y-auto'
            : 'overflow-hidden'
        }`}
      >
        {/* Full-height separator line between Geography and data columns */}
        {(hasGeographies || showGeographyEmptyState) && (
          <div className="sticky left-[271px] top-12 h-[calc(100%-3rem)] w-px bg-neutral-200 z-[21] pointer-events-none float-left -ml-px" />
        )}

        <div
          role="table"
          aria-label="Analytics place of service data by geography"
          className="grid relative z-10 w-full"
          style={{
            gridTemplateColumns:
              dataColumns.length === 0
                ? '272px 1fr'
                : `272px ${getGridTemplateColumns()}`,
            gridAutoFlow: 'row',
          }}
        >
          <PulseHeader
            label="Geography"
            isSticky
            stickyPosition="both"
            className="z-30 bg-neutral-50 text-neutral-900 border-b border-r border-b-neutral-200 border-r-neutral-200"
            sortable
            sortDirection={sort?.sort === 'name' ? sort.order : null}
            resizable={false}
            menu={
              <PulseColumnMenu
                columnId="name"
                isOpen={openMenuId === 'name'}
                onToggle={(e) => handleEllipsisClick(e, 'name')}
                customSectionComponents={[
                  <SortSection
                    key="sort"
                    currentOrder={sort?.order || null}
                    isActive={sort?.sort === 'name'}
                    onChange={(order) => handleSortToggle('name', order)}
                  />,
                ]}
              />
            }
          />

          {dataColumns.map((col, i) => {
            const sortValue = `${volumeTypes?.[0] || 'claims'}.${dataKeys[i]}`;
            const isMenuOpen = openMenuId === sortValue;
            const isActiveSortColumn = sort?.sort === sortValue;

            return (
              <PulseHeader
                key={dataKeys[i]}
                label={col}
                isSticky
                stickyPosition="top"
                className="z-20 bg-neutral-50 text-neutral-800 border-b border-r border-b-neutral-200 border-r-neutral-200"
                sortable
                sortDirection={isActiveSortColumn ? sort.order : null}
                resizable={true}
                onStartResize={(e) => startResizing(dataKeys[i], e)}
                isResizing={isResizing}
                menu={
                  <PulseColumnMenu
                    columnId={sortValue}
                    isOpen={isMenuOpen}
                    onToggle={(e) => handleEllipsisClick(e, sortValue)}
                    customSectionComponents={[
                      <SortSection
                        key="sort"
                        currentOrder={sort?.order || null}
                        isActive={isActiveSortColumn}
                        onChange={(order) => handleSortToggle(sortValue, order)}
                      />,
                    ]}
                  />
                }
              />
            );
          })}

          {/* Filler Column Header - empty cell to fill remaining space */}
          <div className="bg-neutral-50 border-b border-b-neutral-200 sticky top-0 z-20" />

          {/* Body of the table */}
          {showGeographyEmptyState && (
            <React.Fragment>
              <div className="flex sticky left-0 items-center justify-center w-[272px] h-[calc(100vh-18.75rem)] text-neutral-500 text-sm font-medium font-['Inter'] leading-5">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Icon icon={faGlobe} className="text-2xl" />
                  <span>Select Geographies</span>
                  <button
                    type="button"
                    onClick={handleOpenTerritoryDrawer}
                    className="px-4 py-2 bg-white text-secondary border border-neutral-200 text-sm font-medium font-['Inter'] rounded-full hover:bg-neutral-100 transition-all duration-300"
                  >
                    Add Geographies
                  </button>
                </div>
              </div>
            </React.Fragment>
          )}
          {/* Code set empty state - State 1: Part of grid when no geographies */}
          {showGeographyEmptyState && showCodeSetEmptyState && (
            <div
              className="flex items-center justify-center bg-white h-[calc(100vh-18.75rem)] text-neutral-500 text-sm font-medium font-['Inter'] leading-5"
              style={{ gridColumn: '2 / -1' }}
            >
              <div className="flex flex-col items-center justify-center gap-2">
                <Icon icon={faChartLine} className="text-2xl" />
                <span>Select Code Sets to see place of service data</span>
              </div>
            </div>
          )}

          {hasGeographies &&
            displayedGeographies.map((geography) => {
              const isRegion = geography.type === 'region';

              return (
                <React.Fragment key={geography.id}>
                  <PulseCell
                    isSticky
                    stickyPosition="left"
                    className="z-30 border-b border-b-neutral-200 bg-white group"
                  >
                    <div className="flex items-center justify-between w-full gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          data-testid={`geography-name-${geography.id}`}
                          className="truncate text-secondary text-sm font-normal font-['Inter'] leading-5"
                          title={geography.name}
                        >
                          {geography.name}
                        </span>
                        {isRegion && (
                          <Tooltip
                            content="This is a region"
                            slotProps={{
                              containerClassName: '!bg-neutral-900 !text-white',
                            }}
                          >
                            <span
                              className="inline-flex items-center text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded-full cursor-default shrink-0"
                              data-testid={`region-badge-${geography.id}`}
                              role="status"
                              aria-label="Region indicator"
                            >
                              Region
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <TableRowActions
                        geographyId={geography.id}
                        location={ANALYTICS_LOCATION.PLACE_OF_SERVICE}
                        isRegion={isRegion}
                      />
                    </div>
                  </PulseCell>

                {dataColumns.map((_, i) => {
                  const placeOfServiceCode = dataKeys[i];
                  const volumeData = geography[volumeTypes?.[0]] as
                    | Record<string, number>
                    | undefined;
                  const value = volumeData?.[placeOfServiceCode];
                  return (
                    <PulseCell
                      key={dataKeys[i]}
                      className={`bg-white ${hasCodeSets ? 'border-b border-r border-b-neutral-200 border-r-neutral-200' : ''}`}
                      data-testid={`place-of-service-cell-${geography.id}-${placeOfServiceCode}`}
                    >
                      {hasCodeSets
                        ? value != null
                          ? handleNumericValue(value)
                          : 'n/a'
                        : null}
                    </PulseCell>
                  );
                })}

                {/* Filler Column Cell - empty cell to fill remaining space */}
                <div className={`bg-white ${hasCodeSets ? 'border-b border-b-neutral-200' : ''}`} />
              </React.Fragment>
              );
            })}
        </div>

        {/* Code Set Empty State Overlay - State 2: Shows when we have geographies but no code sets */}
        {!showGeographyEmptyState && showCodeSetEmptyState && (
          <div
            className="absolute left-72 top-12 right-0 bottom-0 z-30 bg-white flex items-center justify-center text-neutral-500 text-sm font-medium font-['Inter'] leading-5"
            data-testid="code-set-empty-state-overlay"
          >
            <div className="flex flex-col items-center justify-center gap-2">
              <Icon icon={faChartLine} className="text-2xl" />
              <span>Select Code Sets to see place of service data</span>
            </div>
          </div>
        )}

        {/* Error Overlay */}
        <AnalyticsErrorOverlay
          error={errorPlaceOfService}
          isVisible={isErrorPlaceOfService && !isLoadingPayerMix && !isFetchingPayerMix}
        />

        {showLoadingOverlay && <PulseLoading />}
      </div>
    </div>
  );
};

export default AnalyticsPlaceOfServiceTable;
